"use client";
import { useState, useRef, useEffect, type FormEvent, type FocusEvent } from "react";
import Autocomplete from "react-google-autocomplete";
import Link from "next/link";
import { PatternFormat } from "react-number-format";
import { TimePickerModal } from "./TimePickerModal";
import { DatePickerModal, formatDateForDisplay } from "./DatePickerModal";
import { validatePhoneNumber } from "../utils/phone-validation";
import {
  displayPickerValue,
  parseTimeToPickerValue,
  pickerValueToTime,
  type PickerValue,
  pickerValuesEqual,
} from "./timePickerUtils";
import { ArrowLeftRightIcon } from "@/app/assets/icons/ArrowLeftRightIcon";
import { ArrowRightIcon } from "@/app/assets/icons/ArrowRightIcon";
import { useSubmitLock } from "../utils/useSubmitLock";
import { submitLead, formatQuoteAddress, type AddressComponent, type TripType } from "../utils/lead-submission";
import { Captcha, type CaptchaHandle } from "./Captcha";

type PlaceResult = {
  address_components?: AddressComponent[];
};

export default function QuoteForm({ theme }: { theme?: "primary" | "secondary" }) {
  const captchaRef = useRef<CaptchaHandle>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [partySize, setPartySize] = useState("");
  const [roundTrip, setRoundtrip] = useState<TripType>("roundtrip");
  const [pickupLocation, setPickupLocation] = useState<AddressComponent[]>([]);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [destinationLocation, setDestinationLocation] = useState<AddressComponent[]>([]);
  const [destinationDate, setDestinationDate] = useState("");
  const [destinationTime, setDestinationTime] = useState("");
  const [pickupPickerOpen, setPickupPickerOpen] = useState(false);
  const [returnPickerOpen, setReturnPickerOpen] = useState(false);
  const [pickupDatePickerOpen, setPickupDatePickerOpen] = useState(false);
  const [returnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
  const [pickupPickerValue, setPickupPickerValue] = useState<PickerValue>({
    hour: "09",
    minute: "00",
    meridiem: "AM",
  });
  const [destinationPickerValue, setDestinationPickerValue] = useState<PickerValue>({
    hour: "09",
    minute: "00",
    meridiem: "AM",
  });
  const clickForm = useRef<(event: FormEvent<HTMLFormElement>) => void>(() => undefined);
  const { submitting, tryStart, finish } = useSubmitLock();
  const [submissionError, setSubmissionError] = useState("");

  // const pickup_ref = usePlacesWidget({
  //   apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY || "",
  //   onPlaceSelected: (place) => {
  //     setPickupLocation_(place.formatted_address)
  //   }
  // });

  const [successMessage, setSuccessMessage] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [partyErrorMessage, setPartyErrorMessage] = useState("");
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [surnameErrorMessage, setSurnameErrorMessage] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [date1ErrorMessage, setDate1ErrorMessage] = useState("");
  const [date2ErrorMessage, setDate2ErrorMessage] = useState("");
  const [location1ErrorMessage, setLocation1ErrorMessage] = useState("");
  const [location2ErrorMessage, setLocation2ErrorMessage] = useState("");
  const [time1ErrorMessage, setTime1ErrorMessage] = useState("");
  const [time2ErrorMessage, setTime2ErrorMessage] = useState("");
  useEffect(() => {
    const dataLayer = window.dataLayer || [];
    clickForm.current = (e) => {
      e.preventDefault();
      dataLayer.push({ event: "quote_submit_2" });
      window.scrollTo(0, 0);
    };
  }, []);
  useEffect(() => {
    if ((pickupLocation.length > 0) && (destinationLocation.length > 0) &&
      JSON.stringify(pickupLocation) === JSON.stringify(destinationLocation)
    ) {
      setLocation2ErrorMessage("Addresses cannot be the same");
    } else {
      setLocation2ErrorMessage("");
    }
  }, [destinationLocation, pickupLocation]);
  useEffect(() => {
    if (roundTrip === "one way" && returnPickerOpen) {
      setReturnPickerOpen(false);
    }
  }, [roundTrip, returnPickerOpen]);

  const input_styles =
    "bg-white px-1/2 py-[.425rem] dt:px-[.75rem] dt:py-[.625rem] mb-[.65rem] dt:mb-1.5 text-1 leading-1 dt:text-1.5 dt:leading-1.5 w-full rounded-1/2 font-normal border-0";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const today = new Date().toISOString().slice(0, 10);

  const checkDate = (date: string) => {
    const selected = new Date(date);
    const comparison = new Date(today);
    return selected < comparison;
  };

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!tryStart()) {
      return;
    }

    setSubmissionError("");
    const digitsOnlyPhone = phone.replace(/\D/g, "");
    const captchaInstance = captchaRef.current;

    try {
      if (
        !emailRegex.test(email) ||
        parseFloat(partySize) < 10 ||
        parseFloat(partySize) > 999 ||
        partySize === "" ||
        (destinationDate === "" && roundTrip === "roundtrip") ||
        (destinationTime === "" && roundTrip === "roundtrip") ||
        pickupDate === "" ||
        pickupTime === "" ||
        name === "" ||
        roundTrip === "roundtrip" && pickupDate === destinationDate && pickupTime === destinationTime ||
        roundTrip === "roundtrip" && pickupDate === destinationDate && pickupTime > destinationTime ||
        surname === "" ||
        digitsOnlyPhone.length !== 10 ||
        email === "" ||
        pickupLocation.length === 0 ||
        name.length < 2 || surname.length < 2 ||
        destinationLocation.length === 0
        || JSON.stringify(pickupLocation) === JSON.stringify(destinationLocation)
      ) {
        if (roundTrip === "roundtrip" && pickupDate === destinationDate) {
          if (pickupTime === destinationTime) {
            setTime2ErrorMessage("Return time cannot be the same as pickup time");
          } else if (pickupTime > destinationTime) {
            setTime2ErrorMessage("Return time must be after the pickup time");
          }
        }
        if (digitsOnlyPhone.length === 0) {
          setPhoneErrorMessage("Phone number cannot be blank");
        } else if (digitsOnlyPhone.length !== 10) {
          setPhoneErrorMessage("Phone numbers must have 10 digits");
        }
        if (email.length > 0 && !emailRegex.test(email)) {
          setEmailErrorMessage("Enter a valid email address");
        }
        if (email === "") {
          setEmailErrorMessage("Email cannot be blank");
        }
        if (
          parseFloat(partySize) < 10 ||
          parseFloat(partySize) > 999 ||
          partySize === ""
        ) {
          setPartyErrorMessage("Party size must be between 10 and 999");
        }

        if (destinationDate === "" && roundTrip === "roundtrip") {
          setDate2ErrorMessage("Return date cannot be blank");
        }
        if (destinationTime === "" && roundTrip === "roundtrip") {
          setTime2ErrorMessage("Return time cannot be blank");
        }
        if (pickupDate === "") {
          setDate1ErrorMessage("Pickup date cannot be blank");
        } else if (checkDate(pickupDate) === true) {
          setDate1ErrorMessage("Pickup date cannot be in the past");
        }
        if (pickupTime === "") {
          setTime1ErrorMessage("Pickup time cannot be blank");
        }
        if (name === "") {
          setNameErrorMessage("First name cannot be blank");
        } else if (name.length < 2) {
          setNameErrorMessage("First name must be at least 2 characters");
        } else {
          setNameErrorMessage("");
        }
        if (surname === "") {
          setSurnameErrorMessage("Last name cannot be blank");
        } else if (surname.length < 2) {
          setSurnameErrorMessage("Last name must be at least 2 characters");
        }
        else {
          setSurnameErrorMessage("");
        }
        if (pickupLocation === null || pickupLocation.length === 0) {
          if (!location1ErrorMessage) {
            setLocation1ErrorMessage("Please enter a complete pickup address including street, city, state, and ZIP code");
          }
        }
        if (destinationLocation === null || destinationLocation.length === 0) {
          if (!location2ErrorMessage) {
            setLocation2ErrorMessage("Please enter a complete destination address including street, city, state, and ZIP code");
          }
        } else if (
          JSON.stringify(pickupLocation) === JSON.stringify(destinationLocation)
        ) {
          setLocation2ErrorMessage("Addresses cannot be the same");
        }
        return;
      }

      const isPhoneValid = await validatePhone(phone);
      if (!isPhoneValid) {
        return;
      }

      let pickup = new Date(pickupDate + "T" + pickupTime);
      let destination = new Date(destinationDate + "T" + destinationTime);
      if (roundTrip === "roundtrip" && pickup > destination) {
        setDate2ErrorMessage("Return date must be after the pickup date");
        return
      }

      const isPast_a = checkDate(pickupDate);
      const isPast_b = roundTrip === "roundtrip" && checkDate(destinationDate);
      if (isPast_a === true || isPast_b === true) {
        if (isPast_a === true) {
          setDate1ErrorMessage("Pickup date cannot be in the past");
        }
        if (isPast_b === true) {
          setDate2ErrorMessage("Return date cannot be in the past");
        }
        return;
      }

      if (!captchaInstance) {
        setSubmissionError("The captcha is not ready. Please reload the page and try again.");
        return;
      }

      let captchaToken: string;
      try {
        captchaToken = await captchaInstance.getToken();
      } catch {
        setSubmissionError("We couldn't verify the captcha. Please reload the page and try again.");
        return;
      }

      try {
        await submitLead({
          kind: "quote",
          firstName: name,
          lastName: surname,
          email,
          phone: digitsOnlyPhone,
          partySize,
          pickupAddress: formatQuoteAddress(pickupLocation),
          destinationAddress: formatQuoteAddress(destinationLocation),
          pickupDate,
          pickupTime,
          ...(roundTrip === "roundtrip"
            ? { tripType: "roundtrip", returnDate: destinationDate, returnTime: destinationTime }
            : { tripType: "one way" }),
        }, captchaToken);
      } catch (error) {
        setSubmissionError(error instanceof Error ? error.message : "We couldn't submit your request.");
        return;
      }

      setEmailErrorMessage("");
      setPartyErrorMessage("");
      setDate1ErrorMessage("");
      setDate2ErrorMessage("");
      setTime1ErrorMessage("");
      setTime2ErrorMessage("");
      setNameErrorMessage("");
      setSurnameErrorMessage("");
      setPhoneErrorMessage("");
      setLocation1ErrorMessage("");
      setLocation2ErrorMessage("");
      setSuccessMessage("Message successfully sent!");
      setEmail("");
      setName("");
      setPhone("");
      setSurname("");
      setPartySize("");
      setPickupDate("");
      setPickupTime("");
      setDestinationDate("");
      setDestinationTime("");
      setPickupLocation([]);
      setDestinationLocation([]);
      setRoundtrip("roundtrip");
      clickForm.current?.(e);
      setSubmissionError("");
    } finally {
      captchaInstance?.reset();
      finish();
    }
  }

  const setPartySize_ = (value: string) => {
    if (value === "" || parseFloat(value) < 10 || parseFloat(value) > 999) {
      setPartyErrorMessage("Party size must be between 10 and 999");
    } else {
      setPartyErrorMessage("");
    }
    setPartySize(value);
  };

  const validateAddressComponents = (components: AddressComponent[]): { isValid: boolean; missingComponents: string[] } => {
    const hasStreetNumber = components.some(c => c.types.includes("street_number"));
    const hasRoute = components.some(c => c.types.includes("route"));
    const hasCity = components.some(c => 
      c.types.includes("locality") || 
      c.types.includes("sublocality_level_1") || 
      c.types.includes("sublocality") ||
      c.types.includes("administrative_area_level_2")
    );
    const hasState = components.some(c => c.types.includes("administrative_area_level_1"));
    const hasZip = components.some(c => c.types.includes("postal_code"));

    const missingComponents: string[] = [];
    if (!hasStreetNumber) missingComponents.push("street number");
    if (!hasRoute) missingComponents.push("street name");
    if (!hasCity) missingComponents.push("city");
    if (!hasState) missingComponents.push("state");
    if (!hasZip) missingComponents.push("zip code");

    // Require street number + route + city + state + zip
    return {
      isValid: hasStreetNumber && hasRoute && hasCity && hasState && hasZip,
      missingComponents
    };
  };

  const setPickupLocation_ = (value: PlaceResult | null) => {
    if (!value?.address_components || value.address_components.length === 0) {
      setLocation1ErrorMessage("Pickup address cannot be blank");
      setPickupLocation([]);
      return;
    }
    
    const validation = validateAddressComponents(value.address_components);
    if (!validation.isValid) {
      setLocation1ErrorMessage(`Please enter a complete address with ${validation.missingComponents.join(", ")}`);
      setPickupLocation([]);
      return;
    }
    
    setLocation1ErrorMessage("");
    setPickupLocation(value.address_components);
  };

  const setDestinationLocation_ = (value: PlaceResult | null) => {
    if (!value?.address_components || value.address_components.length === 0) {
      setLocation2ErrorMessage("Destination address cannot be blank");
      setDestinationLocation([]);
      return;
    }
    
    const validation = validateAddressComponents(value.address_components);
    if (!validation.isValid) {
      setLocation2ErrorMessage(`Please enter a complete address with ${validation.missingComponents.join(", ")}`);
      setDestinationLocation([]);
      return;
    }
    
    setLocation2ErrorMessage("");
    setDestinationLocation(value.address_components);
  };

  const setPickupDate_ = (value: string) => {
    const isPast = checkDate(value);
    if (value === "") {
      setDate1ErrorMessage("Pickup date cannot be blank");
      return;
    } else if (isPast === true && !value.includes("0002") && !value.includes("0020") && !value.includes("0202") && !value.includes("0200")) {
      setDate1ErrorMessage("Pickup date cannot be in the past");
      setPickupDate(value);
      // return;
    }
    else {
      setDate1ErrorMessage("");
      setPickupDate(value);
    }
  };

  const setPickupTime_ = (value: string, syncPicker = false) => {
    if (value === "") {
      setTime1ErrorMessage("Pickup time cannot be blank");
    } else {
      setTime1ErrorMessage("");
    }
    setPickupTime(value);
    if (syncPicker) {
      setPickupPickerValue(parseTimeToPickerValue(value));
    }
  };

  const setDestinationDate_ = (value: string) => {
    if (value === "") {
      setDate2ErrorMessage("Destination date cannot be blank");
    } else {
      setDate2ErrorMessage("");
    }
    setDestinationDate(value);
  };

  const setDestinationTime_ = (value: string, syncPicker = false) => {
    if (value === "") {
      setTime2ErrorMessage("Destination time cannot be blank");
    } else {
      setTime2ErrorMessage("");
    }
    setDestinationTime(value);
    if (syncPicker) {
      setDestinationPickerValue(parseTimeToPickerValue(value));
    }
  };

  const handlePickupPickerChange = (nextValue: PickerValue) => {
    const normalized = { ...nextValue };
    if (!pickerValuesEqual(pickupPickerValue, normalized)) {
      setPickupPickerValue(normalized);
    }
  };

  const openPickupPicker = () => {
    setPickupPickerValue(parseTimeToPickerValue(pickupTime));
    setPickupPickerOpen(true);
  };

  const closePickupPicker = () => {
    setPickupTime_(pickerValueToTime(pickupPickerValue), true);
    setPickupPickerOpen(false);
  };

  const handleDestinationPickerChange = (nextValue: PickerValue) => {
    const normalized = { ...nextValue };
    if (!pickerValuesEqual(destinationPickerValue, normalized)) {
      setDestinationPickerValue(normalized);
    }
  };

  const openReturnPicker = () => {
    if (roundTrip === "one way") {
      return;
    }
    setDestinationPickerValue(parseTimeToPickerValue(destinationTime));
    setReturnPickerOpen(true);
  };

  const closeReturnPicker = () => {
    if (roundTrip === "roundtrip") {
      setDestinationTime_(pickerValueToTime(destinationPickerValue), true);
    }
    setReturnPickerOpen(false);
  };

  const setRoundtrip_ = (value: TripType) => {
    setRoundtrip(value);
  };

  const setEmail_ = (value: string) => {
    if (value === "") {
      setEmailErrorMessage("Email cannot be blank");
    } else {
      setEmailErrorMessage("");
    }
    setEmail(value);
  };

  // const setName_ = (value: string) => {
  //   if (value === "") {
  //     setNameErrorMessage("Name cannot be blank");
  //   } else {
  //     setNameErrorMessage("");
  //   }
  //   setName(value);
  //   const name_array = name.split(" ");
  //   setFirstName(name_array[0]);
  //   setLastName(name_array[1]);
  // }

  async function validatePhone(value: string): Promise<boolean> {
    const digitsOnly = value.replace(/\D/g, "");

    if (digitsOnly.length === 0) {
      setPhoneErrorMessage("Phone number cannot be blank");
      return false;
    }

    if (digitsOnly.length !== 10) {
      setPhoneErrorMessage("Phone numbers must have 10 digits");
      return false;
    }

    const result = await validatePhoneNumber(digitsOnly);
    if (result === "invalid") {
      setPhoneErrorMessage("Invalid phone number");
      return false;
    }

    setPhoneErrorMessage("");
    return true;
  }

  const setSurname_ = (value: string) => {
    if (value === "") {
      setSurnameErrorMessage("Last name cannot be blank");
    } else if (value.length < 2) {
      setSurnameErrorMessage("Last name must be at least 2 characters");
    }
    else {
      setSurnameErrorMessage("");
    }
    setSurname(value);
  };

  const setName_ = (value: string) => {
    if (value === "") {
      setNameErrorMessage("First name cannot be blank");
    } else if (value.length < 2) {
      setNameErrorMessage("First name must be at least 2 characters");
    } else {
      setNameErrorMessage("");
    }
    setName(value);
  }

  const removeError = (_event: FocusEvent<HTMLInputElement>) => {
    // if (e.target.name === "departure_date") {
    //   setDate1ErrorMessage("");
    // }
    // if (e.target.name === "departure_time") {
    //   setTime1ErrorMessage("");
    // }
    // if (e.target.name === "return_date") {
    //   setDate2ErrorMessage("");
    // }
    // if (e.target.name === "return_time") {
    //   setTime2ErrorMessage("");
    // }
  };

  const setPhone_ = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    setPhone(value);

    if (digitsOnly === "") {
      setPhoneErrorMessage("Phone number cannot be blank");
      return;
    }

    if (digitsOnly.length < 10) {
      setPhoneErrorMessage("");
      return;
    }

    if (digitsOnly.length === 10) {
      setPhoneErrorMessage("");
      void validatePhone(value);
    }
  };

  const isSecondaryTheme = theme === "secondary";

  return (
    <div
      className={`flex dt:mt-3 mx-auto ${isSecondaryTheme ? "": "py-[5.5rem] dt:py-3"} dt:px-1/2 w-[75%] dt:w-[66.67%] relative  ${successMessage ? "h-screen" : "h-full"
        }`}
    >
      <TimePickerModal
        heading="Select pickup time"
        isOpen={pickupPickerOpen}
        onClose={closePickupPicker}
        value={pickupPickerValue}
        onChange={handlePickupPickerChange}
      />
      <TimePickerModal
        heading="Select return time"
        isOpen={returnPickerOpen}
        onClose={closeReturnPicker}
        value={destinationPickerValue}
        onChange={handleDestinationPickerChange}
      />
      <DatePickerModal
        isOpen={pickupDatePickerOpen}
        onClose={() => setPickupDatePickerOpen(false)}
        value={pickupDate}
        onChange={(value) => {
          setPickupDate_(value);
          setPickupDatePickerOpen(false);
        }}
        minDate={today}
        label="Select pickup date"
      />
      <DatePickerModal
        isOpen={returnDatePickerOpen}
        onClose={() => setReturnDatePickerOpen(false)}
        value={destinationDate}
        onChange={(value) => {
          setDestinationDate_(value);
          setReturnDatePickerOpen(false);
        }}
        minDate={today}
        label="Select return date"
      />
      {successMessage ? (
        <p className="text-center w-full absolute top-1/2 -translate-y-1/2 text-1 leading-1 dt:text-1.5 dt:leading-1.5  text-white mx-auto">
          Thank you!
          <br />
          <br />
          Your request has been sent.
          <br /> Our team will follow up with you.
          <br />
          <br />
          <Link
            href="/"
            className="uppercase font-bold mt-1 text-3/4 leading-3/4 dt:text-1 dt:leading-1 mx-auto underline-offset-1/4 text-white hover:underline"
          >
            Back to homepage
          </Link>
        </p>
      ) : (
        <div className="flex flex-col w-full">
          <div className="flex w-full relative">
            <form className="flex flex-col w-full relative" id="getQuote" noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Captcha ref={captchaRef} />
                <div role="group" className="grid grid-cols-2 gap-x-1/2 dt:flex mb-[.65rem] dt:mb-1.5">
                  <input
                    type="radio"
                    className="hidden"
                    name="btnradio"
                    value="Roundtrip"
                    id="btnradio1"
                    onChange={() => setRoundtrip_("roundtrip")}
                    checked={roundTrip === "roundtrip"}
                  />
                  <label
                    htmlFor="btnradio1"
                    className="flex items-center cursor-pointer font-normal text-1 leading-1 dt:text-1.5 dt:leading-1.5 text-white"
                  >
                    <span className="w-1.5 h-1.5 mr-1/2 inline-block rounded-full border border-white flex-no-shrink "></span>
                    Roundtrip
                  </label>
                  <input
                    type="radio"
                    className="hidden"
                    name="btnradio"
                    value="One way"
                    id="btnradio2"
                    onChange={() => setRoundtrip_("one way")}
                    checked={roundTrip === "one way"}
                  />
                  <label
                    htmlFor="btnradio2"
                    className="flex dt:mt-[.125rem] items-center cursor-pointer font-normal text-1 leading-1 dt:text-1.5 dt:leading-1.5 text-white"
                  >
                    <span className="w-1.5 h-1.5 mr-1/2 inline-block ml-1/4 dt:ml-1.5 rounded-full border border-white flex-no-shrink"></span>
                    One way
                  </label>
                </div>
              <h4 className="uppercase font-bold text-3/4 leading-1.25 dt:leading-1.5 dt:text-1 text-white tracking-[0.06rem] dt:tracking-widest">
                What&apos;s your party size?
              </h4>
              <div className="">
                <input
                  id="partySize"
                  name="partySize"
                  type="number"
                  placeholder={"10 or more"}
                  onChange={(e) => setPartySize_(e.target.value)}
                  value={partySize}
                  className={`${input_styles}  ${partyErrorMessage && "text-red placeholder:!text-red"
                    }`}
                />


                {partyErrorMessage && (
                  <p className="text-white fwGridItem -mt-1/2 text-3/4 leading-3/4 dt:-mt-1.5 dt:text-1 dt:leading-1.5">{partyErrorMessage}</p>
                )}
              </div>
              <h4 className="uppercase font-bold text-3/4 leading-1.25 dt:leading-1.5 dt:text-1 text-white tracking-[0.06rem] dt:tracking-widest">
                Where do you want to be picked up?
              </h4>
              <Autocomplete
                apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ""}
                className={`${input_styles} ${location1ErrorMessage && "text-red placeholder:!text-red"
                  }`}
                libraries={["places"]}
                options={{
                  componentRestrictions: { country: "US" },
                  fields: ["address_components", "geometry", "icon", "name"],
                  types: ["address"],
                  strictBounds: true,
                }}
                name="pickup_address"
                onPlaceSelected={(place: PlaceResult | null) => {
                  setPickupLocation_(place);
                }}
              />
              {location1ErrorMessage && (
                <p className="text-white fwGridItem -mt-1/2 text-3/4 leading-3/4 dt:-mt-1.5 dt:text-1 dt:leading-1.5">{location1ErrorMessage}</p>
              )}
              <h4 className="uppercase font-bold text-3/4 leading-1.25 dt:leading-1.5 dt:text-1 text-white tracking-[0.06rem] dt:tracking-widest">
                What’s your pickup date and time?
              </h4>
              <div className="grid grid-cols-2 gap-x-1/2">
                <input type="hidden" name="departure_date" value={pickupDate} />
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={pickupDatePickerOpen}
                  onClick={(event) => {
                    event.preventDefault();
                    setPickupDatePickerOpen(true);
                  }}
                  className={`${input_styles} flex items-center justify-between text-left ${
                    pickupDate ? "text-black" : "text-grey"
                  } ${date1ErrorMessage && "text-red date-error"}`}
                >
                  <span>
                    {pickupDate ? formatDateForDisplay(pickupDate) : "Select date"}
                  </span>
                </button>
                <div className="relative">
                  <input type="hidden" name="departure_time" value={pickupTime} />
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={pickupPickerOpen}
                    onClick={(event) => {
                      event.preventDefault();
                      openPickupPicker();
                    }}
                    className={`${input_styles} flex items-center justify-between text-left ${pickupTime ? "text-black" : "text-grey"
                      } ${time1ErrorMessage && "text-red placeholder:!text-red date-error"
                      }`}
                  >
                    <span>
                      {pickupTime
                        ? displayPickerValue(pickupPickerValue)
                        : "Select time"}
                    </span>
                  </button>
                </div>{" "}
                {(date1ErrorMessage || time1ErrorMessage) && (
                  <div className="text-white fwGridItem grid grid-cols-2 gap-x-1/2 -mt-1/2 text-3/4 leading-3/4 dt:-mt-1.5 dt:text-1 dt:leading-1.5">
                    <p className="text-white">{date1ErrorMessage}</p>
                    <p className="text-white">{time1ErrorMessage}</p>
                  </div>
                )}
              </div>
              <h4 className="uppercase font-bold text-3/4 leading-1.25 dt:leading-1.5 dt:text-1 text-white tracking-[0.06rem] dt:tracking-widest">
                What&apos;s your destination?
              </h4>
              <Autocomplete
                apiKey={process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ""}
                className={`${input_styles} ${location2ErrorMessage && "text-red placeholder:!text-red"
                  }`}
                libraries={["places"]}
                name="destination_address"
                options={{
                  componentRestrictions: { country: "US" },
                  fields: ["address_components", "geometry", "icon", "name"],
                  types: ["address"],
                  strictBounds: true,
                }}
                onPlaceSelected={(place: PlaceResult | null) => {
                  setDestinationLocation_(place);
                }}
              />
              {location2ErrorMessage && (
                <p className="text-white fwGridItem -mt-1/2 text-3/4 leading-3/4 dt:-mt-1.5 dt:text-1 dt:leading-1.5">{location2ErrorMessage}</p>
              )}
              <h4
                className={`uppercase font-bold text-3/4 leading-1.25 dt:leading-1.5 dt:text-1 text-white tracking-[0.06rem] dt:tracking-widest ${roundTrip === "one way" && "hidden"
                  }`}
              >
                what&apos;s your departure date and time?
              </h4>
              <div className="grid grid-cols-2 gap-x-1/2">
                <input type="hidden" name="return_date" value={destinationDate} />
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={returnDatePickerOpen}
                  onClick={(event) => {
                    event.preventDefault();
                    if (roundTrip === "roundtrip") {
                      setReturnDatePickerOpen(true);
                    }
                  }}
                  className={`${input_styles} flex items-center justify-between text-left ${
                    destinationDate ? "text-black" : "text-grey"
                  } ${date2ErrorMessage && "text-red date-error"} ${roundTrip === "one way" && "hidden"}`}
                >
                  <span>
                    {destinationDate ? formatDateForDisplay(destinationDate) : "Select date"}
                  </span>
                </button>
                <div
                  className={`relative ${roundTrip === "one way" ? "hidden" : ""
                    }`}
                >
                  <input type="hidden" name="return_time" value={destinationTime} />
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={returnPickerOpen}
                    onClick={(event) => {
                      event.preventDefault();
                      openReturnPicker();
                    }}
                    className={`${input_styles} flex items-center justify-between text-left ${destinationTime ? "text-black" : "text-grey"
                      } ${time2ErrorMessage && "text-red placeholder:!text-red date-error"
                      }`}
                  >
                    <span>
                      {destinationTime
                        ? displayPickerValue(destinationPickerValue)
                        : "Select time"}
                    </span>
                  </button>
                </div>

                {(date2ErrorMessage || time2ErrorMessage) &&
                  roundTrip === "roundtrip" && (
                    <div className="text-white fwGridItem grid grid-cols-2 gap-x-1/2 -mt-1/2 text-3/4 leading-3/4 dt:-mt-1.5 dt:text-1 dt:leading-1.5">
                      <p className="text-white">{date2ErrorMessage}</p>
                      <p className="text-white">{time2ErrorMessage}</p>
                    </div>
                  )}
              </div>
              <h4 className="uppercase font-bold text-3/4 leading-1.25 dt:leading-1.5 dt:text-1 text-white tracking-[0.06rem] dt:tracking-widest">
                Personal details
              </h4>
              <div className="grid grid-cols-2 gap-x-1/2">
                <div>
                  <input
                    type="text"
                    placeholder={"First name"}
                    name="first_name"
                    maxLength={40}
                    onChange={(e) => setName_(e.target.value)}
                    value={name}
                    className={`${input_styles} !mb-1/2 ${nameErrorMessage && "text-red placeholder:!text-red"
                      }`}
                  />

                  {nameErrorMessage && (
                    <p className="text-white -mt-1/4 dt:-mt-1/2 text-3/4 leading-3/4 dt:text-1 dt:leading-1.5">{nameErrorMessage}</p>
                  )}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder={"Last name"}
                    name="last_name"
                    maxLength={80}
                    onChange={(e) => setSurname_(e.target.value)}
                    value={surname}
                    className={`${input_styles} !mb-1/2 ${surnameErrorMessage && "text-red placeholder:!text-red"
                      }`}
                  />

                  {surnameErrorMessage && (
                    <p className="text-white -mt-1/4 dt:-mt-1/2 text-3/4 leading-3/4 dt:text-1 dt:leading-1.5">{surnameErrorMessage}</p>
                  )}
                </div>
              </div>
              <input
                name="email"
                type="email"
                maxLength={80}
                placeholder={"Email"}
                onChange={(e) => setEmail_(e.target.value)}
                value={email}
                className={`${input_styles} !mb-1/2 ${emailErrorMessage && "text-red placeholder:!text-red"
                  }`}
              />

              {emailErrorMessage && (
                <p className="text-white -mt-1/4 dt:-mt-1/2 text-3/4 leading-3/4 dt:text-1 dt:leading-1.5">{emailErrorMessage}</p>
              )}
              <PatternFormat
                name="phone"
                type="tel"
                format="(###) ###-####"
                mask="_"
                placeholder={"Phone number"}
                value={phone}
                className={`${input_styles} !mb-1/2 ${phoneErrorMessage && "text-red placeholder:!text-red"
                  }`}
                //  onKeyDown={(event) => {return (event.charCode !=8 && event.charCode ==0 || (event.charCode >= 48 && event.charCode <= 57))}}
                onValueChange={(value) => setPhone_(value.formattedValue)}
              />
              {/* <input
              name="phone"
                type="phone"
                onChange={(e) => setPhone_(e.target.value)}
                value={phone}
                minLength={10}
                maxLength={10}
                required
              /> */}
              {phoneErrorMessage && (
                <p className="text-white -mt-1/4 dt:-mt-1/2 text-3/4 leading-3/4 dt:text-1 dt:leading-1.5">{phoneErrorMessage}</p>
              )}
              {submissionError && (
                <p className="text-white -mt-1/4 dt:-mt-1/2 text-3/4 leading-3/4 dt:text-1 dt:leading-1.5">{submissionError}</p>
              )}
              <div className="flex justify-center">
                <button
                  aria-label="Submit form"
                  type="submit"
                  disabled={submitting === true}
                  id="submitGetQuote"
                  className={`${submitting === true ? 'bg-[#EE9993]' : isSecondaryTheme ? 'bg-blue' : 'bg-red'} w-full px-1/2 dt:px-1.5 py-[.503rem] dt:py-1 mt-1.5 dt:mt-2.5 text-white rounded-1/2 uppercase font-bold text-1 dt:text-1.5 leading-1 tracking-wider`}
                >
                  {submitting === false ? 'Submit' : 'Submitting...'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
